import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { FilterTypeOperator } from '@monetize/types/common';
import isObject from 'lodash/isObject';
import { forwardRef, useId, useImperativeHandle, useReducer } from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import { DatePicker } from '../../DatePicker/DatePicker';
import MButton from '../../MButton';
import MCustomIconButton from '../../MCustomIconButton';
import { MCustomSelect } from '../../MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../../MCustomSelect/components/MainSearchInputV2';
import MFormField from '../../MFormField';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { MFlex } from '../../chakra';
import {
  DataTableFilterRef,
  DateFilterComparison,
  DateFilterData,
  FilterProps,
  isBetweenComparison,
  isBlankNotBlankComparison,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

// Stable value to avoid re-renders
const EMPTY_ARRAY: Array<{ label: string; value: string | number | boolean }> =
  [];

// Map FilterTypeOperator to DateFilterComparison
const mapOperatorToComparison = (
  operator: string,
): DateFilterComparison | null => {
  switch (operator) {
    case FilterTypeOperator.EQ:
      return DateFilterComparison.EQUALS;
    case FilterTypeOperator.GT:
      return DateFilterComparison.GREATER_THAN;
    case FilterTypeOperator.GTE:
      return DateFilterComparison.GREATER_THAN_EQUAL;
    case FilterTypeOperator.LT:
      return DateFilterComparison.LESS_THAN;
    case FilterTypeOperator.LTE:
      return DateFilterComparison.LESS_THAN_EQUAL;
    case FilterTypeOperator.BETWEEN:
      return DateFilterComparison.BETWEEN_INCLUSIVE;
    default:
      return null;
  }
};

// Base comparison values for server-side filtering
const SERVER_COMPARISON_VALUES = [
  { title: 'Equals', value: DateFilterComparison.EQUALS },
  { title: 'Greater Than', value: DateFilterComparison.GREATER_THAN },
  {
    title: 'Greater Than or Equal',
    value: DateFilterComparison.GREATER_THAN_EQUAL,
  },
  { title: 'Less Than', value: DateFilterComparison.LESS_THAN },
  { title: 'Less Than or Equal', value: DateFilterComparison.LESS_THAN_EQUAL },
  {
    title: 'Between (Inclusive)',
    value: DateFilterComparison.BETWEEN_INCLUSIVE,
  },
];

// Shared comparison values between both server and client filtering
const SHARED_COMPARISON_VALUES = [
  ...SERVER_COMPARISON_VALUES,
  {
    title: 'Between (Inclusive)',
    value: DateFilterComparison.BETWEEN_INCLUSIVE,
  },
];

// Additional comparison values for client-side filtering only
const CLIENT_ONLY_COMPARISON_VALUES = [
  { title: 'Is Blank', value: DateFilterComparison.IS_BLANK },
  { title: 'Is Not Blank', value: DateFilterComparison.IS_NOT_BLANK },
];

// Helper function to get appropriate comparison values based on clientControlled
const getComparisonValues = (clientControlled?: boolean) => {
  if (clientControlled) {
    return [...CLIENT_ONLY_COMPARISON_VALUES, ...SHARED_COMPARISON_VALUES];
  }
  return SERVER_COMPARISON_VALUES;
};

interface DateFilterState {
  comparison: DateFilterComparison;
  firstDate: string;
  secondDate: string;
}

type DateFilterAction =
  | { type: 'SET_COMPARISON'; payload: DateFilterComparison }
  | { type: 'SET_FIRST_DATE'; payload: string }
  | { type: 'SET_SECOND_DATE'; payload: string }
  | { type: 'RESET' }
  | { type: 'SET_ALL'; payload: DateFilterState };

const initialFilterState: DateFilterState = {
  comparison: DateFilterComparison.GREATER_THAN,
  firstDate: '',
  secondDate: '',
};

const dateFilterReducer = (
  state: DateFilterState,
  action: DateFilterAction,
): DateFilterState => {
  switch (action.type) {
    case 'SET_COMPARISON':
      return { ...state, comparison: action.payload };
    case 'SET_FIRST_DATE':
      return { ...state, firstDate: action.payload };
    case 'SET_SECOND_DATE':
      return { ...state, secondDate: action.payload };
    case 'RESET':
      return initialFilterState;
    case 'SET_ALL':
      return action.payload;
    default:
      return state;
  }
};

/**
 * Set Filter Component
 */
export const DataTableHeaderDateFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table, clientControlled }, ref) => {
  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (
        columnKey === column.id &&
        isObject(value) &&
        'comparison' in value &&
        'firstDate' in value
      ) {
        column.setFilterValue(value);
      }
    },
  }));

  const key = getColumnFilterKey(column);
  const filterValues =
    table.options.meta?.filterValues?.get(key) || EMPTY_ARRAY;
  const includeTime = !!column.columnDef.meta?.dateFilter?.includeTime;

  const { open, onOpen, onClose } = useDisclosure();

  // Get initial state from meta.selectedFilters if it exists
  const getInitialState = (): DateFilterState => {
    const existingFilter = table.options.meta?.selectedFilters?.get(key);
    if (
      existingFilter &&
      typeof existingFilter === 'object' &&
      'operator' in existingFilter
    ) {
      const mappedComparison = mapOperatorToComparison(
        existingFilter.operator as string,
      );
      return {
        comparison: mappedComparison ?? initialFilterState.comparison,
        firstDate: existingFilter?.['min'] ?? '',
        secondDate: existingFilter?.['max'] ?? '',
      } as DateFilterState;
    }
    return initialFilterState;
  };

  // Store applied state separately from working state
  const [appliedState, setAppliedState] = useReducer(
    dateFilterReducer,
    getInitialState(),
  );

  const [selectedState, dispatchSelected] = useReducer(
    dateFilterReducer,
    getInitialState(),
  );

  useNonInitialEffect(() => {
    // Only update column filter when popover is closed (filters are applied)
    if (!open) {
      const dateFilterData: DateFilterData = {
        comparison: selectedState.comparison,
        firstDate: selectedState.firstDate,
        secondDate: selectedState.secondDate,
      };
      column.setFilterValue(dateFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedState.comparison,
    selectedState.firstDate,
    selectedState.secondDate,
    open,
  ]);

  const handleResetFilters = () => {
    dispatchSelected({ type: 'RESET' });
    setAppliedState({ type: 'RESET' });

    const dateFilterData: DateFilterData = {
      comparison: initialFilterState.comparison,
      firstDate: null,
      secondDate: null,
    };

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, dateFilterData);
    }

    // Close the popover directly
    onClose();
  };

  const handleApplyFilters = () => {
    // Update applied state to match selected state
    setAppliedState({ type: 'SET_ALL', payload: selectedState });

    const dateFilterData: DateFilterData = {
      comparison: selectedState.comparison,
      firstDate: selectedState.firstDate,
      secondDate: selectedState.secondDate,
    };

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, dateFilterData);
    }
    onClose();
  };

  const handleClose = () => {
    // Revert to last applied state when closing without applying
    dispatchSelected({ type: 'SET_ALL', payload: appliedState });
    onClose();
  };

  const filtersApplied =
    isBlankNotBlankComparison(appliedState.comparison) ||
    (!!filterValues &&
      appliedState.firstDate &&
      (!isBetweenComparison(appliedState.comparison) ||
        !!appliedState.secondDate));

  // Check if current state differs from initial state
  const hasChanges =
    selectedState.comparison !== initialFilterState.comparison ||
    selectedState.firstDate !== initialFilterState.firstDate ||
    selectedState.secondDate !== initialFilterState.secondDate;

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter</Popover.Header>
              <Popover.Body minW="304px">
                <MFormField label="Comparison">
                  <MCustomSelect
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInputV2}
                    items={getComparisonValues(clientControlled)}
                    itemTitle="title"
                    itemValue="value"
                    value={selectedState.comparison}
                    onBlur={(ev) => ev.preventDefault()}
                    onChange={(value: any) =>
                      dispatchSelected({
                        type: 'SET_COMPARISON',
                        payload: value as DateFilterComparison,
                      })
                    }
                  />
                </MFormField>
                {!isBlankNotBlankComparison(selectedState.comparison) && (
                  <>
                    <MFormField
                      pt={2}
                      label={
                        isBetweenComparison(selectedState.comparison)
                          ? 'Start'
                          : undefined
                      }
                    >
                      <DatePicker
                        value={selectedState.firstDate}
                        onChange={(value) => {
                          dispatchSelected({
                            type: 'SET_FIRST_DATE',
                            payload: value ?? '',
                          });
                        }}
                        includeTime={includeTime}
                        clearable
                      />
                    </MFormField>
                    {isBetweenComparison(selectedState.comparison) && (
                      <MFormField pt={2} label="End">
                        <DatePicker
                          value={selectedState.secondDate}
                          onChange={(value) => {
                            dispatchSelected({
                              type: 'SET_SECOND_DATE',
                              payload: value ?? '',
                            });
                          }}
                          includeTime={includeTime}
                          clearable
                        />
                      </MFormField>
                    )}
                  </>
                )}
              </Popover.Body>
              <Popover.Footer>
                <MFlex justifyContent="space-between">
                  <MButton
                    variant="tertiary"
                    size="sm"
                    onClick={handleResetFilters}
                    isDisabled={!hasChanges}
                  >
                    Reset
                  </MButton>
                  <MButton size="sm" onClick={handleApplyFilters}>
                    Apply
                  </MButton>
                </MFlex>
              </Popover.Footer>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
